import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import surveyService from "../services/survey-service";
import { getSurveyIdentity } from "../utils/surveyIdentity";
import { useUser } from "../components/context/UserContext";
import { handleErrors } from "../utils/errorHandler";
import "../styles/home-buyer-survey.css";

const SURVEY_QUESTIONS = [
  {
    id: "q1",
    number: "1",
    type: "radio",
    required: true,
    label: "What best describes your current home-buying intent?",
    hint: "Select one",
    options: [
      "Actively looking to buy (1-3 months)",
      "Exploring options casually",
      "Exploring options casually. Will buy if I get a great deal",
      "Interested, but not looking immediately",
      "Not currently looking",
    ],
  },
  {
    id: "q2",
    number: "2",
    type: "radio",
    required: true,
    label: "Which location are you primarily considering?",
    hint: "Select one",
    options: ["Whitefield", "Sarjapur", "Not sure yet", "Other"],
    hasOther: true,
  },
  {
    id: "q3",
    number: "3",
    type: "checkbox",
    required: true,
    label: "What type of purchase are you considering?",
    hint: "Select all that apply",
    options: [
      "Ready-to-move-in home",
      "New launch / under-construction project with construction linked payment plan",
      "Not sure yet",
    ],
  },
  {
    id: "q4",
    number: "4",
    type: "radio",
    required: true,
    label: "What is your primary purpose for buying a home?",
    hint: "Select one",
    options: [
      "Self-use",
      "Investment",
      "Rental income",
      "For parents / family member",
    ],
  },
  {
    id: "q5",
    number: "5",
    type: "radio",
    required: true,
    label: "Which buyer category best describes you?",
    hint: "Select one",
    options: [
      "Working professional",
      "Couple without children",
      "Couple with young children",
      "Family with older children",
      "NRI / outstation buyer",
      "Investor",
    ],
  },
  {
    id: "q6",
    number: "6",
    type: "radio",
    required: true,
    label: "What type of apartment are you currently considering?",
    hint: "Select one",
    options: ["1 BHK", "2 BHK", "3 BHK", "Not sure yet"],
  },
  {
    id: "q7",
    number: "7",
    type: "radio",
    required: true,
    label: "If you were buying a 1 BHK, which layout would you prefer?",
    hint: "Select one",
    options: [
      "1 BHK with 1 attached toilet",
      "1 BHK with 2 toilets, one attached to the bedroom and one common toilet (10% more expensive though)",
      "Either is fine, depending on price and layout",
    ],
  },
  {
    id: "q8",
    number: "8",
    type: "radio",
    required: true,
    label: "What would be the ideal size for a 1 BHK ?",
    hint: "Select one",
    options: ["550–650 sq. ft.", "650–750 sq. ft.", "750+ sq. ft.", "Not sure"],
  },
  {
    id: "q9",
    number: "9",
    type: "radio",
    required: true,
    label: "If you were buying a 1 BHK, which budget would you prefer?",
    hint: "Select one",
    options: [
      "₹75–90L",
      "₹90L–1 Cr",
      "₹1–1.2 Cr",
      "₹1.2 Cr+",
      "I would not consider a 1 BHK",
    ],
  },
  {
    id: "q10",
    number: "10",
    type: "radio",
    required: true,
    label: "What would be the ideal size for a 2 BHK ?",
    hint: "Select one",
    options: [
      "900–1,100 sq. ft.",
      "1,100–1,300 sq. ft.",
      "1,300+ sq. ft.",
      "Not sure",
    ],
  },
  {
    id: "q11",
    number: "11",
    type: "radio",
    required: true,
    label: "If you were buying a 2 BHK which budget would you prefer?",
    hint: "Select one",
    options: [
      "₹1.5–1.75 Cr",
      "₹1.75–2 Cr",
      "₹2 Cr+",
      "Depends on the layout and usable space",
    ],
  },
  {
    id: "q12",
    number: "12",
    type: "radio",
    required: true,
    label: "What would be the ideal size for a 3 BHK ?",
    hint: "Select one",
    options: [
      "1,500–1,700 sq. ft.",
      "1,700–1,900 sq. ft.",
      "1,900+ sq. ft.",
      "Not sure",
    ],
  },
  {
    id: "q13",
    number: "13",
    type: "radio",
    required: true,
    label: "If you were buying a 3 BHK which budget would you prefer?",
    hint: "Select one",
    options: [
      "₹2.5–2.75 Cr",
      "₹2.75 Cr+",
      "Depends on the layout and usable space",
    ],
  },
  {
    id: "q14",
    number: "14",
    type: "radio",
    required: true,
    label: "Which statement best describes your apartment preference?",
    hint: "Select one",
    options: [
      "I prefer fewer rooms with larger living spaces",
      "I prefer more rooms, even if individual rooms are slightly smaller",
      "I prefer a flexible study / work room over an additional bedroom",
      "I prefer larger bedrooms than larger living spaces",
    ],
  },
  {
    id: "q15",
    number: "15",
    type: "radio",
    required: true,
    label: "Which type of apartment project would you prefer?",
    hint: "Select one",
    options: [
      "A project with premium amenities and lifestyle facilities, even if monthly maintenance is higher",
      "A project with basic but well-maintained facilities and lower monthly maintenance",
      "Depends on the overall apartment price, maintenance cost, and facilities offered",
      "Not sure",
    ],
  },
  {
    id: "q16",
    number: "16",
    type: "checkbox",
    required: true,
    maxSelect: 5,
    label:
      "Which amenities or facilities would matter most to you in an apartment project?",
    hint: "Select up to 5",
    options: [
      "Gym / fitness centre",
      "Swimming pool",
      "Clubhouse / community hall",
      "Indoor games / recreation room",
      "Children's play area",
      "Landscaped garden / open spaces",
      "Walking track / jogging path",
      "Sustainability features",
      "Senior citizen seating / activity area",
      "EV charging points",
      "Yoga / meditation space",
      "Visitor parking",
      "Terrace garden / rooftop seating",
      "Party deck / outdoor gathering area",
      "Pet-friendly spaces",
      "Sports facilities, such as badminton, basketball, or pickleball",
      "Convenience store / daily essentials store",
    ],
  },
  {
    id: "q17",
    number: "17",
    type: "radio",
    required: true,
    label: "If two apartments were similarly priced, which would you prefer?",
    hint: "Select one",
    options: [
      "A smaller apartment in a premium amenity-led project",
      "A larger apartment in a project with basic facilities",
    ],
  },
];

const TOTAL_QUESTIONS = SURVEY_QUESTIONS.length;

const formatAnswerValue = (value) => {
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value || "").trim();
};

const buildSurveyPayload = (answers, identity, isCompleted = true) => ({
  userGuid: identity.userGuid || null,
  userEmail: answers.email?.trim() || identity.userEmail || "",
  mobileNumber: identity.mobileNumber || "",
  deviceId: identity.deviceId,
  platform: "Web",
  q01_BuyingIntent: formatAnswerValue(answers.q1),
  q02_Location: formatAnswerValue(answers.q2),
  q02_LocationOther: formatAnswerValue(answers.q2_other),
  q03_PurchaseType: formatAnswerValue(answers.q3),
  q04_Purpose: formatAnswerValue(answers.q4),
  q05_BuyerCategory: formatAnswerValue(answers.q5),
  q06_ApartmentType: formatAnswerValue(answers.q6),
  q07_OneBhkLayout: formatAnswerValue(answers.q7),
  q08_OneBhkSize: formatAnswerValue(answers.q8),
  q09_OneBhkBudget: formatAnswerValue(answers.q9),
  q10_TwoBhkSize: formatAnswerValue(answers.q10),
  q11_TwoBhkBudget: formatAnswerValue(answers.q11),
  q12_ThreeBhkSize: formatAnswerValue(answers.q12),
  q13_ThreeBhkBudget: formatAnswerValue(answers.q13),
  q14_ApartmentPreference: formatAnswerValue(answers.q14),
  q15_ProjectType: formatAnswerValue(answers.q15),
  q16_Amenities: formatAnswerValue(answers.q16),
  q17_PricedPreference: formatAnswerValue(answers.q17),
  isCompleted,
});

const buildInitialAnswers = () => {
  const answers = { email: "" };
  SURVEY_QUESTIONS.forEach((q) => {
    answers[q.id] = q.type === "checkbox" ? [] : "";
    if (q.hasOther) answers[`${q.id}_other`] = "";
  });
  return answers;
};

const HomeBuyerSurvey = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [answers, setAnswers] = useState(buildInitialAnswers);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const identity = getSurveyIdentity(user);
    if (identity.userEmail) {
      setAnswers((prev) =>
        prev.email ? prev : { ...prev, email: identity.userEmail },
      );
    }
  }, [user]);

  const handleRadioChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
      ...(value !== "Other" ? { [`${questionId}_other`]: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [questionId]: "" }));
  };

  const handleCheckboxChange = (questionId, value, checked, maxSelect) => {
    setAnswers((prev) => {
      const current = prev[questionId] || [];

      if (checked && maxSelect && current.length >= maxSelect) {
        toast.info(`You can select up to ${maxSelect} options.`);
        return prev;
      }

      const next = checked
        ? [...current, value]
        : current.filter((item) => item !== value);

      return {
        ...prev,
        [questionId]: next,
        ...(value === "Other" && !checked
          ? { [`${questionId}_other`]: "" }
          : {}),
      };
    });
    setErrors((prev) => ({ ...prev, [questionId]: "" }));
  };

  const handleOtherChange = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [`${questionId}_other`]: value,
    }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!answers.email.trim()) {
      nextErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answers.email.trim())) {
      nextErrors.email = "Enter a valid email address";
    }

    SURVEY_QUESTIONS.forEach((q) => {
      if (!q.required) return;

      const value = answers[q.id];
      const isEmpty =
        q.type === "checkbox" ? !value?.length : !String(value || "").trim();

      if (isEmpty) {
        nextErrors[q.id] = "This question is required";
        return;
      }

      if (q.maxSelect && value.length > q.maxSelect) {
        nextErrors[q.id] = `Please select up to ${q.maxSelect} options`;
      }

      const otherSelected =
        q.type === "checkbox" ? value.includes("Other") : value === "Other";

      if (otherSelected && !answers[`${q.id}_other`]?.trim()) {
        nextErrors[q.id] = "Please specify your answer for Other";
      }
    });

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleClear = () => {
    if (window.confirm("Clear form? Your answers will be removed.")) {
      setAnswers(buildInitialAnswers());
      setErrors({});
      setIsSubmitted(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.error("Please fill all required fields.");
      const firstError = document.querySelector(".survey-card--error");
      firstError?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    setIsSubmitting(true);

    try {
      const identity = getSurveyIdentity(user);
      const payload = buildSurveyPayload(answers, identity, true);
      const response = await surveyService.submitSurvey(payload);

      if (response?.data?.isSuccess === false) {
        throw new Error(response?.data?.message || "Failed to submit survey.");
      }

      setIsSubmitted(true);
      toast.success("Thank you! Your response has been recorded.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const apiMessage = err?.response?.data?.message;
      toast.error(
        apiMessage || handleErrors(err) || "Something went wrong. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <>
        <Helmet>
          <title>Bangalore Home Buyer Preference Survey | Werentt</title>
        </Helmet>
        <div className="survey-page">
          <section className="survey-hero">
            <div className="container">
              <h1>Bangalore Home Buyer Preference Survey</h1>
            </div>
          </section>
          <div className="survey-container">
            <div className="survey-card survey-card--header survey-success-wrap">
              <div className="survey-success-icon" aria-hidden="true">
                ✓
              </div>
              <h2>Submitted</h2>
              <p className="survey-success-msg">
                Thank you for sharing your preferences — this helps us design
                better living spaces across Bangalore.
              </p>
              <button
                type="button"
                className="survey-go-home-btn"
                onClick={() => navigate("/")}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Bangalore Home Buyer Preference Survey | Werentt</title>
        <meta
          name="description"
          content="Share your home buying preferences in Bangalore. A short survey to help design better living spaces across the city."
        />
        <link rel="canonical" href="https://www.werentt.com/survey" />
      </Helmet>

      <div className="survey-page">
        <section className="survey-hero">
          <div className="container">
            <h1>Bangalore Home Buyer Preference Survey</h1>
            <p>
              Dear Respondent, We are conducting a brief survey to understand
              your preferences and expectations for buying a home in Bangalore.
              Your valuable feedback will help us design better living spaces
              that meet your needs across the city. It will take only about 2
              minutes to complete this survey. Thank you for your time and
              cooperation.
            </p>
          </div>
        </section>

        <form className="survey-container" onSubmit={handleSubmit} noValidate>
          <div className="survey-card survey-card--header">
            <span className="survey-meta">
              ~ 2 minutes · {TOTAL_QUESTIONS} questions
            </span>
            <h2>Tell us what you’re looking for</h2>
            <p>
              Answer a few quick questions about location, budget, layout, and
              amenities. Fields marked with * are required.
            </p>
          </div>

          <div
            className={`survey-card ${errors.email ? "survey-card--error" : ""}`}
          >
            <label className="survey-question-label" htmlFor="survey-email">
              Email <span className="required">*</span>
            </label>
            <p className="survey-hint-label">Required</p>
            <input
              id="survey-email"
              type="email"
              className="survey-text-input"
              placeholder="Your email"
              value={answers.email}
              onChange={(e) => {
                setAnswers((prev) => ({ ...prev, email: e.target.value }));
                setErrors((prev) => ({ ...prev, email: "" }));
              }}
            />
            <p className="survey-hint">
              This will be recorded with your response.
            </p>
            {errors.email && (
              <p className="survey-error-text">{errors.email}</p>
            )}
          </div>

          {SURVEY_QUESTIONS.map((question) => {
            const selected = answers[question.id];
            const otherSelected =
              question.type === "checkbox"
                ? selected?.includes("Other")
                : selected === "Other";

            return (
              <div
                key={question.id}
                className={`survey-card ${
                  errors[question.id] ? "survey-card--error" : ""
                }`}
              >
                <p className="survey-question-label">
                  {question.number ? `${question.number}. ` : ""}
                  {question.label}{" "}
                  {question.required && <span className="required">*</span>}
                </p>
                {question.hint && (
                  <p className="survey-hint-label">{question.hint}</p>
                )}

                <div className="survey-options">
                  {question.options.map((option) => {
                    const inputId = `${question.id}-${option}`;
                    const isOther = option === "Other";
                    const checked =
                      question.type === "checkbox"
                        ? selected?.includes(option)
                        : selected === option;
                    const atMax =
                      question.type === "checkbox" &&
                      question.maxSelect &&
                      selected?.length >= question.maxSelect &&
                      !checked;

                    return (
                      <div key={option} className="survey-option">
                        <label
                          className={`survey-option-label ${
                            atMax ? "is-disabled" : ""
                          }`}
                          htmlFor={inputId}
                        >
                          <input
                            id={inputId}
                            type={question.type}
                            name={question.id}
                            value={option}
                            checked={checked}
                            disabled={atMax}
                            onChange={(e) => {
                              if (question.type === "checkbox") {
                                handleCheckboxChange(
                                  question.id,
                                  option,
                                  e.target.checked,
                                  question.maxSelect,
                                );
                              } else {
                                handleRadioChange(question.id, option);
                              }
                            }}
                          />
                          <span className="survey-option-text">{option}</span>
                        </label>

                        {isOther && otherSelected && (
                          <input
                            type="text"
                            className="survey-other-input"
                            placeholder="Please specify"
                            value={answers[`${question.id}_other`] || ""}
                            onChange={(e) =>
                              handleOtherChange(question.id, e.target.value)
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                </div>

                {question.maxSelect && (
                  <p className="survey-hint">
                    Selected {selected?.length || 0} of {question.maxSelect}
                  </p>
                )}

                {errors[question.id] && (
                  <p className="survey-error-text">{errors[question.id]}</p>
                )}
              </div>
            );
          })}

          <div className="survey-actions">
            <button
              type="submit"
              className="survey-submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
            <button
              type="button"
              className="survey-clear-btn"
              onClick={handleClear}
            >
              Clear form
            </button>
          </div>

          <p className="survey-footer-note">
            Never submit passwords through this form. Your responses help
            Werentt understand buyer preferences across Bangalore.
          </p>
        </form>
      </div>
    </>
  );
};

export default HomeBuyerSurvey;
